<template>
  <div class="ui-panel wsp">
    <!-- Poslije slanja: link se vidi samo ovdje (u bazi je samo hash) -->
    <template v-if="result">
      <div v-if="channel === 'email'" class="ui-banner wsp-ok" role="status">
        <v-icon size="16" color="currentColor">mdi-check-circle-outline</v-icon>
        <span>{{ $t("supplier_po.sent_email_ok", { to: (result.dispatch.recipients || []).join(", ") }) }}</span>
      </div>

      <ui-section :title="$t('supplier_po.link_ready')">
        <div class="wsp-link">
          <input ref="link" class="ui-input" readonly :value="result.url" :aria-label="$t('supplier_po.link_ready')" @focus="$event.target.select()" />
          <button type="button" class="ui-btn" @click="copy">
            <v-icon size="16" color="currentColor">{{ copied ? "mdi-check" : "mdi-content-copy" }}</v-icon>
            {{ copied ? $t("supplier_po.copied") : $t("supplier_po.copy") }}
          </button>
        </div>
        <small class="ui-muted">{{ $t("supplier_po.link_once") }}</small>
      </ui-section>

      <div v-if="channel === 'viber' || channel === 'whatsapp'" class="ui-actions">
        <a v-if="channel === 'viber'" class="ui-btn wsp-viber" :href="result.share.viber">
          <v-icon size="16" color="currentColor">mdi-phone-message</v-icon>
          {{ $t("supplier_po.open_viber") }}
        </a>
        <a v-if="channel === 'whatsapp'" class="ui-btn wsp-whatsapp" :href="result.share.whatsapp" target="_blank" rel="noopener">
          <v-icon size="16" color="currentColor">mdi-whatsapp</v-icon>
          {{ $t("supplier_po.open_whatsapp") }}
        </a>
      </div>

      <div class="ui-form-actions">
        <button type="button" class="ui-btn ui-btn--primary" @click="close">{{ $t("supplier_po.close") }}</button>
      </div>
    </template>

    <form v-else class="ui-form" novalidate @submit.prevent="send">
      <ui-banner v-if="!document.supplier_id" tone="info">{{ $t("supplier_po.no_supplier") }}</ui-banner>

      <ui-section :title="$t('supplier_po.channel_title')">
        <ui-segment v-model="channel" block :options="channels" :label="$t('supplier_po.channel_title')">
          <template #option="{ option }">
            <v-icon size="16" color="currentColor">{{ option.icon }}</v-icon>
            {{ option.label }}
          </template>
        </ui-segment>
      </ui-section>

      <div v-if="channel === 'email'" class="ui-field">
        <label for="wsp-rcpt">{{ $t("supplier_po.recipients") }}</label>
        <div class="wsp-chips" :class="{ 'is-invalid': !!recipientsError }" @click="$refs.rcpt && $refs.rcpt.focus()">
          <span v-for="(email, index) in recipients" :key="email" class="ui-chip wsp-chip">
            {{ email }}
            <button type="button" class="wsp-chip__x" :aria-label="$t('delete') + ': ' + email" @click.stop="removeRecipient(index)">
              <v-icon size="12" color="currentColor">mdi-close</v-icon>
            </button>
          </span>
          <input
            id="wsp-rcpt"
            ref="rcpt"
            v-model="draft"
            type="email"
            inputmode="email"
            autocomplete="off"
            :aria-invalid="String(!!recipientsError)"
            @keydown="onRecipientKey"
            @blur="commitDraft"
            @paste="onPaste"
          />
        </div>
        <span v-if="recipientsError" class="ui-field__err" role="alert">{{ recipientsError }}</span>
        <span v-else class="ui-field__hint">{{ $t("supplier_po.recipients_hint") }}</span>
        <ui-banner v-if="document.supplier_id && !supplierEmail" tone="warn">{{ $t("supplier_po.no_supplier_email") }}</ui-banner>
      </div>

      <div class="ui-field">
        <label for="wsp-msg">{{ $t("supplier_po.message") }}</label>
        <textarea id="wsp-msg" v-model="message" class="wsp-textarea" rows="3" maxlength="1000"></textarea>
        <span class="ui-field__hint wsp-count">{{ message.length }} / 1000</span>
      </div>

      <div class="ui-field">
        <label id="wsp-days-l">{{ $t("supplier_po.expires") }}</label>
        <ui-segment v-model="days" labelledby="wsp-days-l" :options="dayOptions" />
      </div>

      <ui-banner v-if="error" role="alert">{{ error }}</ui-banner>

      <div class="ui-form-actions">
        <button type="button" class="ui-btn" @click="close">{{ $t("cancel") }}</button>
        <button type="submit" class="ui-btn ui-btn--primary" :disabled="loading">
          <v-icon size="16" color="currentColor">{{ loading ? "mdi-loading mdi-spin" : "mdi-send" }}</v-icon>
          {{ channel === "email" ? $t("supplier_po.send_email") : $t("supplier_po.create_link") }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import warehouseService from "@/services/warehouse";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_RECIPIENTS = 5;

/**
 * Slanje narudžbenice dobavljaču (panel novog magacina, na telefonu u donjem listu): mejl (šalje backend) ili link za
 * Viber / WhatsApp / bilo koji drugi kanal. Dobavljač odgovara na javnom linku bez prijave.
 * `onDone` se zove kad je link napravljen (lista se osvježava), `onClose` zatvara donji list na telefonu.
 */
export default {
  name: "WarehouseSupplierSendPanel",
  inject: { shell: { from: "reportShell", default: null } },
  props: {
    document: { type: Object, required: true },
    onDone: { type: Function, default: null },
    onClose: { type: Function, default: null },
  },
  data() {
    return {
      channel: "email",
      recipients: [],
      draft: "",
      message: "",
      days: 30,
      loading: false,
      error: "",
      recipientsError: "",
      result: null,
      copied: false,
    };
  },
  computed: {
    supplier() {
      if (this.document.supplier) {
        return this.document.supplier;
      }
      const list = (this.$store.state.supplier && this.$store.state.supplier.suppliers) || [];
      return list.find((supplier) => supplier.id === this.document.supplier_id) || null;
    },
    supplierEmail() {
      return (this.supplier && this.supplier.email) || "";
    },
    channels() {
      return [
        { value: "email", label: this.$t("supplier_po.channel_email"), icon: "mdi-email-outline" },
        { value: "viber", label: "Viber", icon: "mdi-phone-message" },
        { value: "whatsapp", label: "WhatsApp", icon: "mdi-whatsapp" },
        { value: "link", label: this.$t("supplier_po.channel_link"), icon: "mdi-link-variant" },
      ];
    },
    dayOptions() {
      return [7, 14, 30, 60].map((n) => ({ value: n, label: this.$t("supplier_po.days", { n }) }));
    },
  },
  created() {
    if (this.supplierEmail) {
      this.recipients = [this.supplierEmail];
    }
  },
  methods: {
    close() {
      if (this.onClose) {
        this.onClose();
      } else if (this.shell) {
        this.shell.closePanel();
      }
    },
    // ------------------------------------------------------------ adrese
    addRecipients(text) {
      const parts = String(text || "")
        .split(/[\s,;]+/)
        .map((part) => part.trim())
        .filter(Boolean);
      const invalid = [];
      parts.forEach((part) => {
        if (!EMAIL.test(part)) {
          invalid.push(part);
        } else if (!this.recipients.some((email) => email.toLowerCase() === part.toLowerCase())) {
          this.recipients.push(part);
        }
      });
      this.draft = invalid.join(" ");
      this.recipientsError = invalid.length ? this.$t("supplier_po.recipients_invalid") : "";
    },
    commitDraft() {
      if (this.draft.trim()) {
        this.addRecipients(this.draft);
      }
    },
    onRecipientKey(event) {
      if (["Enter", ",", ";", " "].includes(event.key)) {
        if (this.draft.trim() || event.key === "Enter") {
          event.preventDefault();
        }
        this.commitDraft();
      } else if (event.key === "Backspace" && !this.draft && this.recipients.length) {
        this.recipients.pop();
      }
    },
    onPaste(event) {
      const text = event.clipboardData && event.clipboardData.getData("text");
      if (text && /[\s,;]/.test(text.trim())) {
        event.preventDefault();
        this.addRecipients(text);
      }
    },
    removeRecipient(index) {
      this.recipients.splice(index, 1);
      this.recipientsError = "";
    },
    validRecipients() {
      this.commitDraft();
      if (this.channel !== "email") {
        return true;
      }
      if (this.draft.trim()) {
        return false;
      }
      if (!this.recipients.length) {
        this.recipientsError = this.$t("supplier_po.recipients_required");
        return false;
      }
      if (this.recipients.length > MAX_RECIPIENTS) {
        this.recipientsError = this.$t("supplier_po.recipients_max");
        return false;
      }
      this.recipientsError = "";
      return true;
    },
    // ------------------------------------------------------------ slanje
    async send() {
      this.error = "";
      if (!this.validRecipients()) {
        return;
      }
      this.loading = true;
      try {
        const payload = {
          channel: this.channel,
          message: this.message.trim() || null,
          expires_in_days: this.days,
        };
        if (this.channel === "email") {
          payload.recipients = [...this.recipients];
        }
        const { data } = await warehouseService.sendDocumentToSupplier(this.document.id, payload);
        this.result = data;
        if (this.onDone) {
          this.onDone(data);
        }
        if (this.channel === "link") {
          this.copy();
        }
      } catch (e) {
        const res = e.response;
        const first = res && res.data && res.data.errors && Object.values(res.data.errors)[0];
        this.error = (first && first[0]) || (res && res.data && res.data.message) || this.$t("supplier_po.send_error");
      } finally {
        this.loading = false;
      }
    },
    async copy() {
      if (!this.result) {
        return;
      }
      try {
        await navigator.clipboard.writeText(this.result.url);
        this.copied = true;
        setTimeout(() => (this.copied = false), 2200);
      } catch (e) {
        // clipboard API nije dostupan (http / stari preglednik): polje se označi za ručno kopiranje
        if (this.$refs.link) {
          this.$refs.link.select();
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.wsp {
  gap: 16px;
}

.wsp-ok {
  background: var(--rm-success-soft);
  color: var(--rm-success);
}

.wsp-link {
  display: flex;
  gap: 8px;

  .ui-input {
    flex: 1;
    min-width: 0;
    font-size: 13px;
  }
}

.wsp-viber {
  background: #7360f2;
  border-color: #7360f2;
  color: #fff;

  &:hover {
    background: #5b49d6 !important;
    color: #fff !important;
  }
}

.wsp-whatsapp {
  background: #1ea952;
  border-color: #1ea952;
  color: #fff;

  &:hover {
    background: #168a42 !important;
    color: #fff !important;
  }
}

/* polje sa oznakama adresa */
.wsp-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-height: 40px;
  padding: 4px 8px;
  border: 1px solid var(--rm-border-strong);
  border-radius: 10px;
  background: #fff;
  cursor: text;

  &:focus-within {
    outline: 2px solid var(--rm-accent);
    outline-offset: 1px;
  }

  &.is-invalid {
    border-color: var(--rm-danger);
    outline: 1px solid var(--rm-danger);
  }

  input {
    flex: 1;
    min-width: 160px;
    height: 30px;
    padding: 0 4px;
    border: 0;
    outline: none;
    background: transparent;
    color: var(--rm-text);
    font-size: 14px;
  }
}

.wsp-chip {
  padding-right: 4px;

  &__x {
    display: inline-grid;
    place-items: center;
    width: 18px;
    height: 18px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: inherit;
    cursor: pointer;

    &:hover {
      background: rgba(28, 25, 23, 0.12);
    }
  }
}

.wsp-textarea {
  width: 100%;
  min-height: 84px;
  padding: 10px 11px;
  border: 1px solid var(--rm-border-strong);
  border-radius: 10px;
  background: #fff;
  color: var(--rm-text);
  font-family: inherit;
  font-size: 14px;
  resize: vertical;

  &:focus-visible {
    outline: 2px solid var(--rm-accent);
    outline-offset: 1px;
  }
}

.wsp-count {
  justify-self: end;
}
</style>
